// FedEx shipping-plan options for the office dropdown.
//
// A real FedEx service catalog, grouped domestic / international, plus the
// flat-rate One Rate envelope. The merchant picks the one matching how they use
// FedEx; we persist the choice as carrier preferences and the backend shipment
// builder reads them. Service names are FedEx brand names (not translated).
//
// Note: this lists FedEx's standard services; if a service isn't enabled on the
// account, the label call fails at ship time. A future version can filter to
// live-available services via the rates API.

export const FEDEX_ONE_RATE_VALUE = "ONE_RATE";

export const FEDEX_DOMESTIC_SERVICES = [
  { serviceType: "FEDEX_GROUND", label: "FedEx Ground" },
  { serviceType: "GROUND_HOME_DELIVERY", label: "FedEx Home Delivery" },
  { serviceType: "FEDEX_EXPRESS_SAVER", label: "FedEx Express Saver" },
  { serviceType: "FEDEX_2_DAY", label: "FedEx 2Day" },
  { serviceType: "FEDEX_2_DAY_AM", label: "FedEx 2Day A.M." },
  { serviceType: "STANDARD_OVERNIGHT", label: "FedEx Standard Overnight" },
  { serviceType: "PRIORITY_OVERNIGHT", label: "FedEx Priority Overnight" },
  { serviceType: "FIRST_OVERNIGHT", label: "FedEx First Overnight" },
] as const;

export const FEDEX_INTERNATIONAL_SERVICES = [
  {
    serviceType: "INTERNATIONAL_ECONOMY",
    label: "FedEx International Economy",
  },
  {
    // Europe-only economy service. Picked outside Europe, the backend falls back
    // to International Economy rather than letting FedEx reject the label.
    serviceType: "FEDEX_REGIONAL_ECONOMY",
    label: "FedEx Regional Economy",
  },
  {
    serviceType: "INTERNATIONAL_PRIORITY",
    label: "FedEx International Priority",
  },
  { serviceType: "INTERNATIONAL_FIRST", label: "FedEx International First" },
  {
    serviceType: "FEDEX_INTERNATIONAL_CONNECT_PLUS",
    label: "FedEx International Connect Plus",
  },
] as const;

export const ALL_SERVICES = [
  ...FEDEX_DOMESTIC_SERVICES,
  ...FEDEX_INTERNATIONAL_SERVICES,
];

const DOMESTIC = new Set<string>(
  FEDEX_DOMESTIC_SERVICES.map((s) => s.serviceType),
);
const INTERNATIONAL = new Set<string>(
  FEDEX_INTERNATIONAL_SERVICES.map((s) => s.serviceType),
);

// The lane a plan value applies to. One Rate is a US flat-rate (domestic-only)
// product; a real service maps to international or domestic by its catalog set.
// Used by the rate viewer to keep origin/destination compatible with the
// selected product (FedEx rejects an intl service on a domestic lane and vice
// versa with SERVICETYPE.NOT.ALLOWED).
export function serviceLaneFor(
  value: string,
): "international" | "domestic" | "oneRate" {
  if (value === FEDEX_ONE_RATE_VALUE) return "oneRate";
  return INTERNATIONAL.has(value) ? "international" : "domestic";
}

// The two selectable lanes in the config UI. One Rate is a US flat-rate product,
// so it lives under the domestic lane.
export type FedexLane = "domestic" | "international";

export function laneOf(value: string): FedexLane {
  return serviceLaneFor(value) === "international" ? "international" : "domestic";
}

// Products offered for a lane. Domestic leads with One Rate (flat-rate), then the
// real domestic services; international lists the intl services.
export function productsForLane(
  lane: FedexLane,
): readonly { serviceType: string; label: string }[] {
  if (lane === "international") return FEDEX_INTERNATIONAL_SERVICES;
  return [
    { serviceType: FEDEX_ONE_RATE_VALUE, label: "FedEx One Rate" },
    ...FEDEX_DOMESTIC_SERVICES,
  ];
}

export const FEDEX_PICKUP_SCHEDULED = "USE_SCHEDULED_PICKUP";
export const FEDEX_PICKUP_DROPOFF = "DROPOFF_AT_FEDEX_LOCATION";

export type FedexPlanCredentials = {
  fedexPlan: string;
  defaultServiceType?: string;
  useParcelPackaging?: boolean;
  incoterm?: string;
  useElectronicTradeDocuments?: boolean;
  useFedexReturn?: boolean;
  pickupType?: string;
};

export type FedexPlanOptions = {
  // Real FedEx return label vs forward label. Default on for a real service;
  // ignored for One Rate (the flat-rate plan forces a forward label).
  fedexReturn?: boolean;
  pickupType?: string;
  // Whether the FedEx account may transmit customs paperwork electronically.
  // Only ever consulted for a forward label: FedEx refuses it on a return.
  useElectronicTradeDocuments?: boolean;
};

// Dropdown value → carrier-preference credentials to PATCH.
// One Rate → the fedexPlan enum (builder derives the flat-rate envelope, keeps
// the forward label); we still forward the pickup preference.
// A service → CUSTOM + the raw fields; return-label + pickup always sent,
// international also sets DAP (recipient). This is a returns product: duties are
// never merchant-paid, so DDP is never used.
export function valueToCredentials(
  value: string,
  {
    fedexReturn = true,
    pickupType = FEDEX_PICKUP_DROPOFF,
    useElectronicTradeDocuments,
  }: FedexPlanOptions = {},
): FedexPlanCredentials {
  if (value === FEDEX_ONE_RATE_VALUE) {
    return { fedexPlan: "ONE_RATE_ENVELOPE", pickupType };
  }
  const isInternational = INTERNATIONAL.has(value);
  return {
    fedexPlan: "CUSTOM",
    defaultServiceType: value,
    useParcelPackaging: true,
    // A return label carries the ETD now: the shipment builder drops
    // RETURN_SHIPMENT instead, which is what FedEx refused it alongside. The
    // lane must still not derive this — whether the account may transmit
    // customs paperwork electronically is a FedEx entitlement, not a property
    // of the route, so only an explicit answer counts.
    useElectronicTradeDocuments: useElectronicTradeDocuments ?? false,
    useFedexReturn: fedexReturn,
    pickupType,
    ...(isInternational ? { incoterm: "DAP" } : {}),
  };
}

// Preselect the dropdown from stored preferences.
export function valueFromPreferences(prefs?: {
  fedexPlan?: string | null;
  defaultServiceType?: string | null;
}): string {
  const svc = prefs?.defaultServiceType;
  if (
    prefs?.fedexPlan === "CUSTOM" &&
    svc &&
    (DOMESTIC.has(svc) || INTERNATIONAL.has(svc))
  ) {
    return svc;
  }
  return FEDEX_ONE_RATE_VALUE;
}
